/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteDimensionPathParameters,
  TLDimensionCreateRequest,
  TLDimensionResponse,
  TLDimensionUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateDimensionPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Dimensions
 */
export const getAllDimensions = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLDimensionResponse[]>(
    { url: `/v2/data-harmonization/dimensions`, method: 'GET', signal },
    options,
  );
};

export const getGetAllDimensionsQueryKey = () => {
  return ['v2', 'data-harmonization', 'dimensions'] as const;
};

export const getGetAllDimensionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllDimensions>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllDimensionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllDimensions>>> = ({ signal }) =>
    getAllDimensions(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllDimensions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllDimensionsQueryResult = NonNullable<Awaited<ReturnType<typeof getAllDimensions>>>;
export type GetAllDimensionsQueryError = ErrorType<unknown>;

export function useGetAllDimensions<TData = Awaited<ReturnType<typeof getAllDimensions>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDimensions>>,
          TError,
          Awaited<ReturnType<typeof getAllDimensions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDimensions<TData = Awaited<ReturnType<typeof getAllDimensions>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDimensions>>,
          TError,
          Awaited<ReturnType<typeof getAllDimensions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDimensions<TData = Awaited<ReturnType<typeof getAllDimensions>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Dimensions
 */

export function useGetAllDimensions<TData = Awaited<ReturnType<typeof getAllDimensions>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllDimensionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Dimensions
 */
export const prefetchGetAllDimensionsQuery = async <
  TData = Awaited<ReturnType<typeof getAllDimensions>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDimensions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllDimensionsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Dimensions
 */
export const invalidateGetAllDimensions = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllDimensionsQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Create Dimension
 */
export const createDimension = (
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDimensionResponse>(
    {
      url: `/v2/data-harmonization/dimensions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLDimensionCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateDimensionQueryKey = (tLDimensionCreateRequest?: BodyType<TLDimensionCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'dimensions', tLDimensionCreateRequest] as const;
};

export const getCreateDimensionQueryOptions = <
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateDimensionQueryKey(tLDimensionCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createDimension>>> = ({ signal }) =>
    createDimension(tLDimensionCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createDimension>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateDimensionQueryResult = NonNullable<Awaited<ReturnType<typeof createDimension>>>;
export type CreateDimensionQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateDimension<
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDimension>>,
          TError,
          Awaited<ReturnType<typeof createDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDimension<
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createDimension>>,
          TError,
          Awaited<ReturnType<typeof createDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateDimension<
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Dimension
 */

export function useCreateDimension<
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateDimensionQueryOptions(tLDimensionCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Dimension
 */
export const prefetchCreateDimensionQuery = async <
  TData = Awaited<ReturnType<typeof createDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateDimensionQueryOptions(tLDimensionCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Dimension
 */
export const invalidateCreateDimension = async (
  queryClient: QueryClient,
  tLDimensionCreateRequest: BodyType<TLDimensionCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateDimensionQueryKey(tLDimensionCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Dimension
 */
export const deleteDimension = (
  { dimensionId }: DeleteDimensionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/dimensions/${dimensionId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteDimensionQueryKey = ({ dimensionId }: DeleteDimensionPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'dimensions', dimensionId] as const;
};

export const getDeleteDimensionQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dimensionId }: DeleteDimensionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteDimensionQueryKey({ dimensionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteDimension>>> = ({ signal }) =>
    deleteDimension({ dimensionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dimensionId !== null && dimensionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteDimensionQueryResult = NonNullable<Awaited<ReturnType<typeof deleteDimension>>>;
export type DeleteDimensionQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteDimension<
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDimensionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDimension>>,
          TError,
          Awaited<ReturnType<typeof deleteDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDimension<
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDimensionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteDimension>>,
          TError,
          Awaited<ReturnType<typeof deleteDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteDimension<
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteDimensionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Dimension
 */

export function useDeleteDimension<
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dimensionId }: DeleteDimensionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteDimensionQueryOptions({ dimensionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Dimension
 */
export const prefetchDeleteDimensionQuery = async <
  TData = Awaited<ReturnType<typeof deleteDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dimensionId }: DeleteDimensionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteDimensionQueryOptions({ dimensionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Dimension
 */
export const invalidateDeleteDimension = async (
  queryClient: QueryClient,
  { dimensionId }: DeleteDimensionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteDimensionQueryKey({ dimensionId }) }, options);

  return queryClient;
};

/**
 * @summary Update Dimension
 */
export const updateDimension = (
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDimensionResponse>(
    {
      url: `/v2/data-harmonization/dimensions/${dimensionId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDimensionUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDimensionQueryKey = (
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest?: BodyType<TLDimensionUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'dimensions', dimensionId, tLDimensionUpdateRequest] as const;
};

export const getUpdateDimensionQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateDimensionQueryKey({ dimensionId }, tLDimensionUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDimension>>> = ({ signal }) =>
    updateDimension({ dimensionId }, tLDimensionUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dimensionId !== null && dimensionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDimensionQueryResult = NonNullable<Awaited<ReturnType<typeof updateDimension>>>;
export type UpdateDimensionQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDimension<
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDimension>>,
          TError,
          Awaited<ReturnType<typeof updateDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDimension<
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDimension>>,
          TError,
          Awaited<ReturnType<typeof updateDimension>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDimension<
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Dimension
 */

export function useUpdateDimension<
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDimensionQueryOptions({ dimensionId }, tLDimensionUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Dimension
 */
export const prefetchUpdateDimensionQuery = async <
  TData = Awaited<ReturnType<typeof updateDimension>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDimension>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDimensionQueryOptions({ dimensionId }, tLDimensionUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Dimension
 */
export const invalidateUpdateDimension = async (
  queryClient: QueryClient,
  { dimensionId }: UpdateDimensionPathParameters,
  tLDimensionUpdateRequest: BodyType<TLDimensionUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDimensionQueryKey({ dimensionId }, tLDimensionUpdateRequest) },
    options,
  );

  return queryClient;
};
